import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, BrokerageIcon, ButtonResponsiveWrapper, Heading, InvestmentPathType, ManageIcon, NewInvestmentPathCard, PencilIcon, RawButton, SelfDirectedIcon, SimpleHint, Text, useResponsive } from '@common-fe/common-fe';

import { RiskControlOptions } from '@/common/constants';
import routes from '@/common/routes';
import Modal from '@/components/elements/AutoBlockedModal/Modal';
import { useHistory } from '@/modules/core/hooks';
import { useMyEnrollments } from '@/modules/transaction';

import { useInvestor } from '../hooks';
import { RISKS_MODELS } from '../investments.types';
import { WarningUpdateRiskPortfolioModal } from '../UpdateRiskPortfolio/WarningUpdateRiskPortfolioModal';

import FeeFooter from './FeeFooter';

interface Props {
  accountId?: string;
  riskModel?: string;
  isSelfDirectedMode?: boolean;
  onChangeRiskModel: () => void;
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
}

const FILTERED_OPTIONS = [InvestmentPathType.BROKERAGE];

const InvestmentSwitchModelModal: React.FC<Props> = ({
  onChangeRiskModel,
  riskModel,
  isSelfDirectedMode,
  accountId,
  visible,
  onSetVisible,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const history = useHistory();
  const { currentMyAccount } = useMyEnrollments(accountId);

  const [isWarningModalShowed, setWarningModalShowed] = useState(false);
  const {
    data: { pendingTradesAmount },
  } = useInvestor(accountId);
  const [currentOption, setCurrentOption] = useState<InvestmentPathType>(
    isSelfDirectedMode ? InvestmentPathType.SELF_DIRECTED : InvestmentPathType.MANAGED
  );
  const handleRedirect = useCallback(() => {
    onSetVisible(false);
    if (isSelfDirectedMode) {
      if (accountId && currentOption === InvestmentPathType.MANAGED) {
        history.push(routes.MANAGED_SWITCH_QUESTIONNAIRE(accountId));
      }
    } else {
      if (accountId && currentOption === InvestmentPathType.SELF_DIRECTED) {
        history.push(routes.SELF_DIRECTED_SWITCH_QUESTIONNAIRES(accountId));
      }
    }
  }, [accountId, currentOption, history, isSelfDirectedMode, onSetVisible]);
  const currentOptions = useMemo(() => {
    return currentMyAccount?.investmentOptionTypes?.filter((key) => !FILTERED_OPTIONS.includes(key)) || [];
  }, [currentMyAccount?.investmentOptionTypes]);

  const renderPreviousSelected = useCallback(
    (planType: InvestmentPathType) => {
      if (isSelfDirectedMode) {
        if (planType !== InvestmentPathType.SELF_DIRECTED) {
          return null;
        }
      } else {
        if (planType !== InvestmentPathType.MANAGED) {
          return null;
        }
      }
      return (
        <Box
          margin={{ top: 's' }}
          background="background3"
          pad={{
            vertical: 'spacing2',
            horizontal: 'spacing6',
          }}
          round={{ size: 'container2Round', corner: 'bottom' }}
          style={{
            position: 'absolute',
            top: -14,
            zIndex: 2,
          }}
        >
          <Text color="textOnColor" size="small" weight={700}>
            Current investment model
          </Text>
        </Box>
      );
    },
    [isSelfDirectedMode]
  );

  const getTitle = useCallback(
    (type: InvestmentPathType) => {
      if (type === InvestmentPathType.MANAGED) {
        return t('Managed');
      }
      if (type === InvestmentPathType.SELF_DIRECTED) {
        return t('Self Directed');
      }
      if (type === InvestmentPathType.BROKERAGE) {
        return t('Brokerage');
      }
      return '';
    },
    [t]
  );

  const getSubTitle = useCallback(
    (type: InvestmentPathType) => {
      if (type === InvestmentPathType.MANAGED) {
        return t('Effort to manage');
      }
      if (type === InvestmentPathType.SELF_DIRECTED) {
        return t('Medium to manage');
      }
      if (type === InvestmentPathType.BROKERAGE) {
        return t('Hard to manage');
      }
      return '';
    },
    [t]
  );
  const getMainText = useCallback(
    (type: InvestmentPathType) => {
      if (type === InvestmentPathType.MANAGED) {
        return t(
          'Investors who prefer to have investments automatically selected, managed & rebalanced an ongoing basis in accordance with age, risk, profile and time horizon.'
        );
      }
      if (type === InvestmentPathType.SELF_DIRECTED) {
        return t(
          'Investors who have the experience and interest to manage their own portfolios by selecting from a preselected menu of investment options covering multiple asset classes to diversify portfolios.'
        );
      }
      if (type === InvestmentPathType.BROKERAGE) {
        return t(
          'Investors experienced at advanced research, filters and trading accross thousands of available investment instruments. Stocks, ETFs, Mutual Funds.'
        );
      }
      return '';
    },
    [t]
  );

  const getIcon = useCallback((type: InvestmentPathType) => {
    if (type === InvestmentPathType.MANAGED) {
      return <ManageIcon />;
    }
    if (type === InvestmentPathType.SELF_DIRECTED) {
      return <SelfDirectedIcon />;
    }
    if (type === InvestmentPathType.BROKERAGE) {
      return <BrokerageIcon />;
    }
    return null;
  }, []);

  const handdleEditRiskModel = useCallback(() => {
    if (pendingTradesAmount) {
      setWarningModalShowed(true);
      return;
    }
    if (onChangeRiskModel) {
      onChangeRiskModel();
    }
  }, [onChangeRiskModel, pendingTradesAmount]);
  const getManagedModelManagement = useCallback(
    (type: InvestmentPathType) => {
      const currentModel = RISKS_MODELS[riskModel as RiskControlOptions];
      if (type === InvestmentPathType.MANAGED && !isSelfDirectedMode && riskModel) {
        return (
          <Box direction="column" align="center" margin={{ bottom: 'spacing12' }}>
            <Box direction="row" margin={{ bottom: 'spacing8' }}>
              <Text size="medium" weight={700}>
                {currentModel?.title}
              </Text>
              <RawButton
                data-testid="switch-risk-model"
                onClick={handdleEditRiskModel}
                style={{ height: '16px' }}
                margin={{ left: 'spacing2' }}
              >
                <PencilIcon color="iconDisabled" height="16px" size="16px" />
              </RawButton>
            </Box>
            <Text size="medium" weight={700}>
              {currentModel?.label}
            </Text>
          </Box>
        );
      }
      return null;
    },
    [handdleEditRiskModel, isSelfDirectedMode, riskModel]
  );
  return (
    <Modal wrapperStyle={{ zIndex: 9999 }} visible={visible} onSetVisible={onSetVisible}>
      <WarningUpdateRiskPortfolioModal visible={isWarningModalShowed} onSetVisible={setWarningModalShowed} />
      <Box data-testid="investment-switch-model_modal" direction="column" align="center">
        <Box margin={{ bottom: 's' }}>
          <Heading level={4} size="medium">
            {t('Investment model')}
          </Heading>
        </Box>
        <Box margin={{ bottom: 'spacing24' }}>
          <Text textAlign="center">
            {t(
              'Updating your investment model will transfer your entire portfolio balance to the investment model selected. This will take 3 - 4 business days to complete. During that time you will not be able to make changes to your investment portfolio. This includes additional buys or sells.'
            )}
          </Text>
        </Box>

        <Box
          pad={
            isMobile
              ? {
                  top: 'spacing8',
                  horizontal: 'spacing8',
                }
              : 'spacing24'
          }
          direction={isMobile ? 'column' : 'row'}
          background="module"
          round="moduleRound"
        >
          {currentOptions.map((option) => {
            return (
              <Box
                key={option}
                data-testId={`investment-switch-model_option_${option}`}
                margin={isMobile ? { bottom: 'spacing8' } : { right: 'spacing12' }}
              >
                <NewInvestmentPathCard
                  key={option}
                  testId={option}
                  disabled={currentOptions.length === 1}
                  checked={currentOption === option}
                  onClick={() => setCurrentOption(option)}
                >
                  {renderPreviousSelected(option)}
                  <Box direction="row" justify="center" margin={{ bottom: 'xs' }}>
                    {getIcon(option)}
                  </Box>
                  <Text size="medium" color="textSecondary" margin={{ bottom: 'm' }}>
                    {getSubTitle(option)}
                  </Text>
                  <Text size="xxlarge" weight="bold" margin={{ bottom: 'xs' }}>
                    {getTitle(option)}
                  </Text>
                  {getManagedModelManagement(option)}
                  <Text textAlign="center" margin={{ horizontal: '1px' }}>
                    {getMainText(option)}
                  </Text>
                </NewInvestmentPathCard>
              </Box>
            );
          })}
        </Box>
        <ButtonResponsiveWrapper justify="end" margin={{ top: 'spacing16' }} fill gap="spacing8">
          <AppButton
            onClick={() => onSetVisible(false)}
            testId="investment-switch-model_cancel"
            width={isMobile ? '100%' : '140px'}
            buttonType="secondary"
          >
            {t('Cancel')}
          </AppButton>
          {currentOptions.length !== 1 &&
            (pendingTradesAmount ? (
              <SimpleHint
                content={
                  <Text>
                    {t(
                      'Investment model cannot be switched until all pending activity has settled. This may take 2-3 business days.'
                    )}
                  </Text>
                }
              >
                <AppButton
                  disabled={true}
                  testId="investment-switch-model_switch"
                  width={isMobile ? '100%' : '140px'}
                >
                  {t('Switch')}
                </AppButton>
              </SimpleHint>
            ) : (
              <AppButton
                disabled={
                  isSelfDirectedMode
                    ? currentOption === InvestmentPathType.SELF_DIRECTED
                    : currentOption === InvestmentPathType.MANAGED
                }
                onClick={handleRedirect}
                testId="investment-switch-model_switch"
                width={isMobile ? '100%' : '140px'}
              >
                {t('Switch')}
              </AppButton>
            ))}
        </ButtonResponsiveWrapper>
        <Box margin={{ top: 'spacing16' }}>
          <FeeFooter />
        </Box>
      </Box>
    </Modal>
  );
};
export default InvestmentSwitchModelModal;
